import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Paper, IconButton, Skeleton, Button } from '@mui/material';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import VideocamOffIcon from '@mui/icons-material/VideocamOff';
import VideocamIcon from '@mui/icons-material/Videocam';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getStreamUrl } from 'api/canvms';
import VideoPlayer from '../VideoPlayer/VideoPlayer';

function CameraCell({ camera, siteId, onFullscreen }) {
  const [streamInfo, setStreamInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // -- Viewport intersection detection ----------------------------------------
  // Only fetch and render the stream when the cell is visible in the viewport.
  // When scrolled out of view, the VideoPlayer is unmounted (destroying the HLS
  // instance) and the stream URL fetch is deferred until the cell becomes visible.
  const cellRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = cellRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { rootMargin: '200px', threshold: 0 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const fetchStream = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getStreamUrl(siteId, camera.id);
      setStreamInfo(response.data);
    } catch (err) {
      console.error('[CANvision] Error fetching stream URL:', err);
      setError('Failed to load camera feed');
    } finally {
      setLoading(false);
    }
  }, [siteId, camera.id]);

  // Only fetch the stream URL when the cell becomes visible
  useEffect(() => {
    if (isVisible && !streamInfo && !error) {
      fetchStream();
    }
  }, [isVisible, streamInfo, error, fetchStream]);

  // Handle fatal HLS errors propagated from VideoPlayer
  const handleStreamError = useCallback(() => {
    setError('Camera stream lost');
  }, []);

  return (
    <Paper
      ref={cellRef}
      elevation={2}
      sx={{
        position: 'relative',
        aspectRatio: '16/9',
        overflow: 'hidden',
        bgcolor: 'grey.900',
      }}
    >
      {/* Camera name overlay */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          p: 1,
          background: 'linear-gradient(rgba(0,0,0,0.7), transparent)',
          zIndex: 1,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="caption" color="white" noWrap>
          {camera.display_name || camera.name || camera.stream_id}
        </Typography>
        <IconButton
          size="small"
          onClick={() => onFullscreen(camera)}
          sx={{ color: 'white' }}
        >
          <FullscreenIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Video content — only rendered when visible in viewport */}
      {!isVisible ? (
        /* Lightweight placeholder while off-screen */
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <VideocamIcon sx={{ fontSize: 36, color: 'grey.600', opacity: 0.4 }} />
        </Box>
      ) : loading ? (
        <Skeleton variant="rectangular" sx={{ width: '100%', height: '100%' }} />
      ) : error ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            gap: 1,
            px: 2,
          }}
        >
          <VideocamOffIcon sx={{ fontSize: 36, color: 'grey.500' }} />
          <Typography variant="caption" color="error" align="center">
            {error}
          </Typography>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchStream}
            sx={{
              color: 'grey.300',
              borderColor: 'grey.600',
              '&:hover': { borderColor: 'grey.400' },
              textTransform: 'none',
            }}
          >
            Retry
          </Button>
        </Box>
      ) : streamInfo ? (
        <VideoPlayer
          streamUrl={streamInfo.stream_url}
          poster={streamInfo.snapshot_url}
          streamType={streamInfo.type || 'hls'}
          refreshInterval={3000} // Slower refresh for grid view (detail view uses 1000ms)
          muted
          onFatalError={handleStreamError}
        />
      ) : null}
    </Paper>
  );
}

export default CameraCell;
